import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { UrlParams, PackageRevenueReportList } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUsePackageRevenue {
  dateStart?: string;
  dateEnd?: string;
  ordering?: string;
  limit?: string;
  enabled?: boolean;
  csvExport?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
}

function usePackageRevenue({
  dateStart,
  dateEnd,
  ordering,
  limit = '20',
  enabled = true,
  csvExport,
}: IUsePackageRevenue): UseInfiniteQueryResult<AxiosResponse<PackageRevenueReportList>> {
  const fetchPackageRevenue = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PackageRevenueReportList>> => {
      const params = pageParam;

      if (dateStart !== undefined) {
        params.start_date = dateStart;
      }
      if (dateEnd !== undefined) {
        params.end_date = dateEnd;
      }
      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      }
      return client.get(`reports/package-revenue?${encodeUrlParams(params)}`);
    },
    [limit, ordering, dateStart, dateEnd],
  );

  return useInfiniteQuery(
    ['package-revenue', dateStart, dateEnd, ordering, csvExport],
    fetchPackageRevenue,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
      enabled: enabled && dateStart !== undefined && dateEnd !== undefined,
      staleTime: 86400000, // 1 day
    },
  );
}

export default usePackageRevenue;
